import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { buildSharedVideoSourceMultipartOptions } from './shared_video_source_multipart';

require('@uppy/core/dist/style.min.css');
require('@uppy/dashboard/dist/style.min.css');

const uploaders = new Map();

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const requestJson = async (url, { method = 'POST', body, signal } = {}) => {
  const response = await fetch(url, {
    method,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken()
    },
    body: body ? JSON.stringify(body) : undefined,
    signal
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload.error || 'video_upload_failed');
    error.status = response.status;
    throw error;
  }

  return payload;
};

const setUploadState = (root, state, message = '') => {
  const states = {
    resume: root.querySelector('[data-video-upload-resume]'),
    progress: root.querySelector('[data-video-upload-progress]'),
    error: root.querySelector('[data-video-upload-error]')
  };

  Object.entries(states).forEach(([name, element]) => {
    if (!element) return;
    element.classList.toggle('hidden', name !== state);
  });
  if (states.error) states.error.textContent = state === 'error' ? message : '';
};

const setElementVisible = (element, visible, displayClass = 'block') => {
  if (!element) return;

  element.classList.toggle('hidden', !visible);
  element.classList.toggle(displayClass, visible);
};

const formatBytes = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '';

  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;

  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const showFallbackFileCard = (root) => {
  const preview = root.querySelector('[data-video-upload-preview]');
  const fileCard = root.querySelector('[data-video-upload-file-card]');

  if (preview) preview.classList.add('hidden');
  if (fileCard) fileCard.classList.remove('hidden');
};

const loadUploadedVideoUrl = async (root, downloadUrlPath) => {
  if (!downloadUrlPath) return;

  try {
    const payload = await requestJson(downloadUrlPath, { method: 'POST' });
    const downloadUrl = payload.download_url;
    const download = root.querySelector('[data-video-upload-download]');
    const preview = root.querySelector('[data-video-upload-preview]');
    const fileCard = root.querySelector('[data-video-upload-file-card]');

    if (downloadUrl && download) download.href = downloadUrl;
    if (!downloadUrl || !preview) return;

    preview.classList.remove('hidden');
    if (fileCard) fileCard.classList.add('hidden');
    preview.onerror = () => showFallbackFileCard(root);
    preview.src = downloadUrl;
    preview.load();
  } catch (error) {
    console.warn('Unable to load shared video source preview', error);
    showFallbackFileCard(root);
  }
};

// shows the Apple media check under the uploader.
// the server sends the reasons already worded, we only lay them out
const appleCheckLine = (tag, text, className) => {
  const line = document.createElement(tag);
  line.className = className;
  line.textContent = text;
  return line;
};

export const showAppleMediaCheck = (root, appleCheck) => {
  const box = root.querySelector('[data-apple-media-check]');
  if (!box) return;

  const status = appleCheck?.status;
  // kept on the box, so the back button brings the page back in the same state
  box.dataset.appleCheckStatus = status || '';
  const lines = [];
  if (status === 'checking') {
    lines.push(appleCheckLine('p', box.dataset.checkingText, 'text-purple-700'));
  } else if (status === 'passed') {
    lines.push(appleCheckLine('p', box.dataset.passedText, 'text-green-700'));
  } else if (status === 'rejected') {
    lines.push(appleCheckLine('p', box.dataset.rejectedText, 'text-red-600'));
    const list = appleCheckLine('ul', '', 'list-disc pl-5 text-red-600');
    (appleCheck.reasons || []).forEach((reason) => {
      list.appendChild(appleCheckLine('li', `${reason.title}. ${reason.message}`, ''));
    });
    lines.push(list);
  } else if (status === 'unavailable') {
    lines.push(appleCheckLine('p', box.dataset.unavailableText, 'text-red-600'));
  }

  box.replaceChildren(...lines);
  setElementVisible(box, lines.length > 0);
  // the editor's Submit and Update Episode wait while the check runs
  box.dispatchEvent(new CustomEvent('apple-media-check:status', { bubbles: true, detail: { status: status || '' } }));
};

const POLL_EVERY_MS = 2000;
const REQUEST_LIMIT_MS = 10000;
// the server itself answers "couldn't check" after 2 minutes, so this only
// covers a page that can't reach the server at all
const GIVE_UP_AFTER_MS = 150000;
const pollers = new Map();

const stopAppleCheckPolling = (root) => {
  const poller = pollers.get(root);
  if (!poller) return;

  clearTimeout(poller.timer);
  poller.controller?.abort();
  pollers.delete(root);
};

// asks the server for the check result of this video until it has one. Never reloads the page,
// so nothing the producer typed is lost
const pollAppleMediaCheck = (root, sourceId) => {
  stopAppleCheckPolling(root);
  if (!sourceId || !root.dataset.videoSourcesUrl) return;

  const poller = { failingSince: null };
  pollers.set(root, poller);
  const url = `${root.dataset.videoSourcesUrl}/${encodeURIComponent(sourceId)}`;

  const ask = async () => {
    poller.controller = new AbortController();
    const limit = setTimeout(() => poller.controller.abort(), REQUEST_LIMIT_MS);
    let source = null;
    let gone = false;
    try {
      source = await requestJson(url, { method: 'GET', signal: poller.controller.signal });
    } catch (error) {
      source = null;
      // the video is gone, or this page can no longer see it (another show opened): asking again can't help
      gone = error.status === 403 || error.status === 404;
    } finally {
      clearTimeout(limit);
    }
    // stopped, or a newer video took its place while we waited: this answer is old
    if (pollers.get(root) !== poller || root.dataset.videoSourceId !== sourceId) return;

    if (gone) {
      // clearing the line brings the buttons back; the server still decides on Submit
      stopAppleCheckPolling(root);
      showAppleMediaCheck(root, null);
      return;
    }

    if (source) {
      poller.failingSince = null;
      if (source.apple_check?.status !== 'checking') {
        // a result, or no check at all (the switch was set to Off)
        stopAppleCheckPolling(root);
        showAppleMediaCheck(root, source.apple_check);
        return;
      }
    } else {
      poller.failingSince ??= Date.now();
      if (Date.now() - poller.failingSince >= GIVE_UP_AFTER_MS) {
        stopAppleCheckPolling(root);
        showAppleMediaCheck(root, { status: 'unavailable' });
        return;
      }
    }
    poller.timer = setTimeout(ask, POLL_EVERY_MS);
  };
  poller.timer = setTimeout(ask, POLL_EVERY_MS);
};

// the editor opened, or came back with the back button, on a video still being checked
const resumeAppleCheckPolling = (root) => {
  if (root.querySelector('[data-apple-media-check]')?.dataset.appleCheckStatus !== 'checking') return;

  pollAppleMediaCheck(root, root.dataset.videoSourceId);
};

const showEmptyUploadState = (root) => {
  const empty = root.querySelector('[data-video-upload-empty]');
  const complete = root.querySelector('[data-video-upload-complete]');
  const actions = root.querySelector('[data-video-upload-actions]');
  const preview = root.querySelector('[data-video-upload-preview]');
  const fileCard = root.querySelector('[data-video-upload-file-card]');
  const download = root.querySelector('[data-video-upload-download]');

  stopAppleCheckPolling(root);
  if (preview) {
    preview.pause?.();
    preview.removeAttribute('src');
    preview.load();
  }
  if (fileCard) fileCard.classList.add('hidden');
  if (download) download.href = '#';
  root.dataset.videoSourceReady = 'false';

  setElementVisible(empty, true, 'flex');
  setElementVisible(complete, false);
  setElementVisible(actions, false, 'flex');
  setUploadState(root, 'ready');
  showAppleMediaCheck(root, null);
};

const showUploadedSourceState = (root, source = {}) => {
  const empty = root.querySelector('[data-video-upload-empty]');
  const complete = root.querySelector('[data-video-upload-complete]');
  const actions = root.querySelector('[data-video-upload-actions]');
  const filename = root.querySelector('[data-video-upload-filename]');
  const cardFilename = root.querySelector('[data-video-upload-card-filename]');
  const cardSize = root.querySelector('[data-video-upload-card-size]');
  const cardType = root.querySelector('[data-video-upload-card-type]');
  const downloadUrlPath = source.download_url_path || root.dataset.videoSourceDownloadUrl;
  const originalFilename = source.original_filename || root.dataset.videoSourceFilename || 'Uploaded video';
  const byteSize = source.byte_size || root.dataset.videoSourceByteSize || '';
  const contentType = source.content_type_hint || root.dataset.videoSourceContentType || '';

  root.dataset.videoSourceDownloadUrl = downloadUrlPath || '';
  root.dataset.videoSourceFilename = originalFilename;
  root.dataset.videoSourceByteSize = byteSize;
  root.dataset.videoSourceContentType = contentType;
  root.dataset.videoSourceReady = 'true';

  if (filename) filename.textContent = originalFilename;
  if (cardFilename) cardFilename.textContent = originalFilename;
  if (cardSize) cardSize.textContent = formatBytes(byteSize);
  if (cardType) cardType.textContent = contentType || 'Video file';

  setElementVisible(empty, false, 'flex');
  setElementVisible(complete, true);
  setElementVisible(actions, true, 'flex');
  setUploadState(root, 'ready');
  showFallbackFileCard(root);
  loadUploadedVideoUrl(root, downloadUrlPath);
};

/**
 * Initializes the shared Apple/YouTube source uploader for one grouped editor.
 *
 * @param {HTMLElement} root upload section containing endpoint and trigger data.
 * @returns {Uppy|null} initialized uploader.
 */
export const initializeSharedVideoSourceUploader = (root) => {
  if (!root || root.dataset.videoSourceUploaderInitialized === 'true') return null;
  if (root.dataset.videoSourceReadOnly === 'true') {
    if (root.dataset.videoSourceDownloadUrl) showUploadedSourceState(root);
    resumeAppleCheckPolling(root);
    return null;
  }

  const trigger = root.querySelector('[data-video-source-trigger]');
  const collectionUrl = root.dataset.videoSourcesUrl;
  const maxVideoBytes = Number(root.dataset.maxVideoBytes);
  if (!trigger || !collectionUrl || !Number.isFinite(maxVideoBytes)) return null;

  const uppy = new Uppy({
    id: `shared-video-source-${collectionUrl}`,
    autoProceed: true,
    allowMultipleUploadBatches: false,
    restrictions: {
      maxFileSize: maxVideoBytes,
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.mp4', '.m4v', '.mov', 'video/mp4', 'video/quicktime']
    },
    locale: {
      strings: {
        youCanOnlyUploadFileTypes: 'Upload an MP4, M4V, or MOV video file.'
      }
    }
  });

  uppy.use(Dashboard, {
    trigger,
    closeAfterFinish: true,
    showProgressDetails: true,
    hideUploadButton: true,
    singleFileFullScreen: true,
    proudlyDisplayPoweredByUppy: false,
    note: 'MP4, M4V, or MOV. Maximum file size: 10 GB.'
  });
  uppy.use(AwsS3, buildSharedVideoSourceMultipartOptions({
    collectionUrl,
    request: requestJson,
    onSourceUpdate: (source) => {
      if (source.status === 'ready') {
        root.dataset.videoSourceId = source.id;
        showUploadedSourceState(root, source);
        showAppleMediaCheck(root, source.apple_check);
        // the save answers at once and the check runs in the background
        if (source.apple_check?.status === 'checking') pollAppleMediaCheck(root, source.id);
      }
    }
  }));

  uppy.on('upload', () => {
    // a new video is on its way, so the old one's result no longer matters
    stopAppleCheckPolling(root);
    setUploadState(root, 'progress');
  });
  // after a cancel, or removing the only file mid-upload, nothing is uploading,
  // so we clear the progress line and any "Checking…"
  const clearUploadInProgress = () => {
    stopAppleCheckPolling(root);
    setUploadState(root, 'ready');
    showAppleMediaCheck(root, null);
  };
  uppy.on('cancel-all', clearUploadInProgress);
  uppy.on('file-removed', () => {
    if (uppy.getFiles().length === 0) clearUploadInProgress();
  });
  uppy.on('complete', (result) => {
    if (result?.successful?.length && !root.dataset.videoSourceFilename) {
      showUploadedSourceState(root, {
        original_filename: result.successful[0].name,
        download_url_path: root.dataset.videoSourceDownloadUrl
      });
    }
  });
  uppy.on('upload-error', (_file, error) => {
    stopAppleCheckPolling(root);
    showAppleMediaCheck(root, null);
    setUploadState(root, 'error', error?.message || 'Your video could not be uploaded. Please try again.');
  });
  uppy.on('restriction-failed', (_file, error) => {
    setUploadState(root, 'error', error?.message || 'Choose an MP4, M4V, or MOV file under 10 GB.');
  });

  root.dataset.videoSourceUploaderInitialized = 'true';
  root.sharedVideoSourceUppy = uppy;
  const replaceButton = root.querySelector('[data-video-upload-replace]');
  if (replaceButton) {
    replaceButton.onclick = () => {
      // same as uppy's own Done button: forget the finished upload so Upload opens the file picker again.
      // a finished upload has no abort listener left, so this never cancels the saved video
      uppy.clearUploadedFiles();
      showEmptyUploadState(root);
    };
  }
  if (root.dataset.videoSourceDownloadUrl) showUploadedSourceState(root);
  resumeAppleCheckPolling(root);
  uploaders.set(root, uppy);
  return uppy;
};

const initializeSharedVideoSourceUploaders = () => {
  document.querySelectorAll('[data-video-source-upload]')
    .forEach(initializeSharedVideoSourceUploader);
};

export const closeSharedVideoSourceUploaders = () => {
  uploaders.forEach((uppy, root) => {
    uppy.close({ reason: 'unmount' });
    delete root.sharedVideoSourceUppy;
    delete root.dataset.videoSourceUploaderInitialized;
  });
  uploaders.clear();
  // a read-only editor polls without an uploader
  pollers.forEach((_poller, root) => stopAppleCheckPolling(root));
};

if (typeof document !== 'undefined') {
  document.addEventListener('turbo:load', initializeSharedVideoSourceUploaders);
  document.addEventListener('turbo:before-cache', closeSharedVideoSourceUploaders);
}
